import { useEffect, useState } from 'react';
import { graphql } from '../gql/index.ts';
import { useQuery } from 'urql';
import { SeminarCard } from './SeminarCard.tsx';
import { Pagination } from './Pagination.tsx';
import { usePublicCompany } from './hooks/usePublicCompany.ts';
import { getFormattedSeminarDate } from './util.ts';
import { ErrorPage } from './ErrorPage.tsx';
import { PublicMenuSidebar } from './PublicMenuSidebar.tsx';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Checkbox, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { ComboBox } from '@progress/kendo-react-dropdowns';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { ParticipantStatus } from '../gql/graphql.ts';
import { Loader } from '@progress/kendo-react-indicators';
import { useDeferredLoading } from '../shared/useDeferredLoading.ts';
import { BackButton } from '../shared/BackButton.tsx';

const FilterOptionsQuery = graphql(/* GraphQL */ `
  query getPublicSeminarFilterOptions($companyId: String!) {
    seminarFilterOptions(companyId: $companyId) {
      locationTexts
      lecturers {
        name
        id
      }
    }
  }
`);

const PublicSeminarsSearchQuery = graphql(/* GraphQL */ `
  query getPublicSeminarsSearch(
    $companyId: String!
    $categoryId: String
    $limit: Int!
    $skip: Int!
    $searchTerm: String
    $dateFrom: DateTime
    $dateTo: DateTime
    $lecturerId: String
    $locationText: String
    $webinarOnly: Boolean
    $seatsAvailable: Boolean
  ) {
    publicSeminars(
      companyId: $companyId
      categoryId: $categoryId
      limit: $limit
      skip: $skip
      searchTerm: $searchTerm
      locationText: $locationText
      lecturerId: $lecturerId
      dateFrom: $dateFrom
      dateTo: $dateTo
      webinarOnly: $webinarOnly
      seatsAvailable: $seatsAvailable
    ) {
      seminars {
        id
        title
        subtitle
        seminarNumber
        dateText
        description
        plainDescription
        locationText
        startDate
        endDate
        isBookedOut
        isAlmostBookedOut
        registrationDate
        canceled
        signUps {
          canceled
        }
        currentUserIsLecturer
        lecturers {
          name
        }
        categories {
          name
        }
        participants {
          id
          status
        }
      }
      count
    }
  }
`);

const pageSize = 5;

interface PublicSearchProps {
  companyId: string;
  categoryId?: string;
}

export const PublicSearch: React.FC<PublicSearchProps> = ({
  companyId,
  categoryId,
}) => {
  // Router Hooks
  const navigate = useNavigate();
  const searchParams = useLocation({
    select: (l) => new URLSearchParams(l.searchStr ?? ''),
  });
  const searchStringFromUrl = searchParams.get('q') || null;
  const pageFromUrl = searchParams.get('listPage');

  // Local state
  const [prevCategoryId, setPrevCategoryId] = useState<string | undefined>(
    categoryId,
  );
  const [searchInputValue, setSearchInputValue] = useState<string | null>(
    searchStringFromUrl,
  );
  const [lecturerFilterValue, setLecturerFilterValue] = useState<string | null>(
    null,
  );
  const [locationFilterValue, setLocationFilterValue] = useState<string | null>(
    null,
  );
  const [filteredLecturers, setFilteredLecturers] = useState<
    Array<{ name: string; id: string }>
  >([]);
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const [dateFromFilterValue, setDateFromFilterValue] = useState<Date | null>(
    null,
  );
  const [dateToFilterValue, setDateToFilterValue] = useState<Date | null>(null);
  const [webinarOnly, setWebinarOnly] = useState(false);
  const [seatsAvailable, setSeatsAvailable] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string | null>(
    searchStringFromUrl,
  );

  const [currentPage, setCurrentPage] = useState(() => {
    const n = pageFromUrl ? parseInt(pageFromUrl, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n - 1 : 0;
  });

  // Mirror page into the URL so "Zurück" from a seminar restores it.
  // The param is named `listPage`, not `page`, because `page` is a reserved
  // WordPress query var: on the WP-embedded site, `?page=N` triggers WordPress'
  // redirect_canonical and the whole deep link is 301'd back to the base URL.
  // Use a number (not a string) — TanStack Router's default search serializer
  // JSON-encodes values, so a string would round-trip as ?listPage="5".
  useEffect(() => {
    const desired = currentPage === 0 ? null : currentPage + 1;
    const currentInUrl = pageFromUrl ? parseInt(pageFromUrl, 10) : null;
    if (
      desired ===
      (Number.isFinite(currentInUrl as number) ? currentInUrl : null)
    )
      return;
    navigate({
      search: ((prev: Record<string, unknown>) => {
        const { listPage: _listPage, ...rest } = prev;
        return desired === null ? rest : { ...rest, listPage: desired };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
      replace: true,
    });
  }, [currentPage, pageFromUrl, navigate]);

  if (prevCategoryId !== categoryId) {
    setPrevCategoryId(categoryId);
    setCurrentPage(0);
  }

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchTerm(searchInputValue),
      400,
    );
    return () => clearTimeout(timer);
  }, [searchInputValue]);

  const [publicSeminarsSearchQueryResult] = useQuery({
    query: PublicSeminarsSearchQuery,
    variables: {
      companyId,
      categoryId,
      limit: pageSize,
      skip: currentPage * pageSize,
      searchTerm: debouncedSearchTerm,
      dateFrom: dateFromFilterValue,
      dateTo: dateToFilterValue,
      lecturerId: lecturerFilterValue,
      locationText: locationFilterValue || null,
      webinarOnly: webinarOnly || null,
      seatsAvailable: seatsAvailable || null,
    },
  });

  const [filterOptionsQueryResult, executeFilterOptionsQuery] = useQuery({
    query: FilterOptionsQuery,
    variables: {
      companyId,
    },
    pause: true,
  });

  const filterOptionsLocation =
    filterOptionsQueryResult.data?.seminarFilterOptions?.locationTexts || [];

  const filterOptionsLecturers =
    filterOptionsQueryResult.data?.seminarFilterOptions?.lecturers || [];

  useEffect(() => {
    // Refresh filter dropdown options when the GraphQL query returns.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilteredLecturers(
      filterOptionsQueryResult.data?.seminarFilterOptions?.lecturers || [],
    );
  }, [filterOptionsQueryResult.data?.seminarFilterOptions?.lecturers]);

  useEffect(() => {
    // Refresh filter dropdown options when the GraphQL query returns.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilteredLocations(
      filterOptionsQueryResult.data?.seminarFilterOptions?.locationTexts || [],
    );
  }, [filterOptionsQueryResult.data?.seminarFilterOptions?.locationTexts]);

  const hasActiveFilters =
    searchInputValue ||
    lecturerFilterValue ||
    locationFilterValue ||
    dateFromFilterValue ||
    dateToFilterValue ||
    webinarOnly ||
    seatsAvailable ||
    categoryId;

  const resetAllFilters = () => {
    setSearchInputValue(null);
    setDebouncedSearchTerm(null);
    setLecturerFilterValue(null);
    setLocationFilterValue(null);
    setDateFromFilterValue(null);
    setDateToFilterValue(null);
    setWebinarOnly(false);
    setSeatsAvailable(false);
    setCurrentPage(0);
    navigate({ to: '/search' });
  };

  const { data } = publicSeminarsSearchQueryResult;
  const { publicSeminars: searchResult } = data || {};
  const [publicCompany] = usePublicCompany();

  // Clamp page if the URL points past the last page of the current result set.
  const resultCount = searchResult?.count;
  if (resultCount != null) {
    const maxPage = resultCount > 0 ? Math.ceil(resultCount / pageSize) - 1 : 0;
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }

  const showSidebar =
    publicCompany?.functionalSettings?.searchActive ||
    Boolean(publicCompany?.sites?.length) ||
    Boolean(publicCompany?.categories?.length);

  const fetching = publicSeminarsSearchQueryResult.fetching;
  const showLoader = useDeferredLoading(fetching);

  return (
    <>
      {publicSeminarsSearchQueryResult.data ? (
        <>
          {showSidebar && (
            <PublicMenuSidebar
              publicCompany={publicCompany}
              hideSearch={true}
            />
          )}
          <div
            className={`${showSidebar ? 'md:col-span-9 md:col-start-4' : 'md:col-span-12'} col-span-1 flex flex-col gap-6`}
          >
            <BackButton href="/" title="Zurück (Katalog)" />
            <h2>Seminare suchen</h2>
            <div className="flex flex-col gap-2">
              <TextBox
                placeholder="Suchbegriff"
                className="*:font-inter *:text-base [&_.k-input-inner]:outline-none"
                value={searchInputValue || ''}
                onChange={(e) => {
                  setSearchInputValue(String(e.value) || '');
                }}
                suffix={() => (
                  <InputSuffix className={`!bg-[var(--kendo-color-base)]`}>
                    <HeroIcon name="MagnifyingGlass" className="mx-3 h-5 w-5" />
                  </InputSuffix>
                )}
                size={'large'}
              />
              <div className="flex gap-6">
                <Checkbox
                  label="Nur online-Schulungen"
                  value={webinarOnly}
                  onChange={(e) => setWebinarOnly(Boolean(e.value))}
                />
                <Checkbox
                  label="Freie Plätze"
                  value={seatsAvailable}
                  onChange={(e) => setSeatsAvailable(Boolean(e.value))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2 md:flex md:w-full">
                <ComboBox
                  onFocus={() => {
                    executeFilterOptionsQuery();
                  }}
                  onChange={(e) => {
                    setLecturerFilterValue(e.target.value?.id || null);
                  }}
                  onFilterChange={(e) => {
                    const filterValue = e.filter.value.toLowerCase();
                    setFilteredLecturers(
                      filterOptionsLecturers.filter((lecturer) =>
                        lecturer.name.toLowerCase().includes(filterValue),
                      ),
                    );
                  }}
                  ariaLabel="Nach Dozent filtern"
                  data={filteredLecturers}
                  id="lecturerFilter"
                  placeholder="Nach Dozent filtern"
                  textField="name"
                  filterable={true}
                  size={'small'}
                  className="!h-[34px] *:font-inter md:flex-[2]"
                />
                <ComboBox
                  onFocus={() => {
                    executeFilterOptionsQuery();
                  }}
                  onChange={(e) => {
                    setLocationFilterValue(e.target.value || null);
                  }}
                  onFilterChange={(e) => {
                    const filterValue = e.filter.value.toLowerCase();
                    setFilteredLocations(
                      filterOptionsLocation.filter((location) =>
                        location.toLowerCase().includes(filterValue),
                      ),
                    );
                  }}
                  ariaLabel="Nach Ort filtern"
                  value={locationFilterValue}
                  data={filteredLocations}
                  id="locationFilter"
                  placeholder="Nach Ort filtern"
                  filterable={true}
                  size={'small'}
                  className="!h-[34px] *:font-inter md:flex-[2]"
                />

                <DatePicker
                  ariaLabel="Startdatum auswählen"
                  id="dateFilterFrom"
                  placeholder="Von"
                  format="dd.MM.yyyy"
                  size={'small'}
                  onChange={(e) => {
                    setDateFromFilterValue(e.value);
                  }}
                  value={dateFromFilterValue}
                  className="md:flex-1"
                />
                <DatePicker
                  ariaLabel="Enddatum auswählen"
                  id="dateFilterTo"
                  placeholder="Bis"
                  format="dd.MM.yyyy"
                  size={'small'}
                  onChange={(e) => {
                    setDateToFilterValue(e.value);
                  }}
                  value={dateToFilterValue}
                  className="md:flex-1"
                />
              </div>

              <button
                type="button"
                onClick={resetAllFilters}
                className="self-end text-sm no-underline hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:no-underline"
                disabled={!hasActiveFilters}
              >
                Alle Filter zurücksetzen
              </button>
            </div>

            {Boolean(searchResult?.count) && (
              <div className="flex gap-4">
                <p className="text-xs">
                  {searchResult?.count === 1
                    ? '1 Ergebnis gefunden'
                    : `${searchResult?.count} Ergebnisse gefunden`}
                </p>
                <div className="flex-1 border-b"></div>
              </div>
            )}

            {(searchResult?.count ?? 0) > 0 ? (
              <>
                {searchResult?.seminars.map((seminar) => {
                  return (
                    <SeminarCard
                      key={seminar?.id}
                      instructors={
                        seminar?.lecturers
                          ?.map((lecturer) => lecturer.name)
                          .filter(Boolean) as string[]
                      }
                      id={seminar?.seminarNumber}
                      title={seminar?.title}
                      time={getFormattedSeminarDate(
                        seminar.startDate,
                        seminar.endDate,
                        seminar.dateText,
                      )}
                      description={seminar?.plainDescription}
                      subtitle={seminar?.subtitle}
                      location={seminar?.locationText}
                      category={
                        seminar?.categories?.find((c) => Boolean(c.name.trim()))
                          ?.name
                      }
                      titleLink={'/seminar/' + seminar?.id}
                      isSignedUp={Boolean(
                        seminar.signUps.filter((s) => !s.canceled).length,
                      )}
                      isParticipant={seminar.participants.some(
                        (p) => p.status === ParticipantStatus.Participant,
                      )}
                      isWaitlist={seminar.participants.some(
                        (p) => p.status === ParticipantStatus.Waitlist,
                      )}
                      isParticipationCanceled={
                        seminar.participants.some(
                          (p) => p.status === ParticipantStatus.Canceled,
                        ) || seminar.signUps.some((s) => s.canceled)
                      }
                      isLecturer={Boolean(seminar.currentUserIsLecturer)}
                      isBookedOut={Boolean(seminar?.isBookedOut)}
                      isAlmostBookedOut={Boolean(seminar?.isAlmostBookedOut)}
                      showSignalLight={
                        publicCompany?.visualSettings.showSignalLight &&
                        !seminar.canceled
                      }
                      registrationDate={seminar?.registrationDate}
                    />
                  );
                })}
              </>
            ) : (
              <ErrorPage text="Keine Seminare gefunden" />
            )}

            {Boolean(searchResult?.count) && (
              <div className="col-span-1 md:col-span-9 md:col-start-4">
                <Pagination
                  onChange={(newPage) => {
                    setCurrentPage(newPage);
                    window.scrollTo({ top: 0, left: 0 });
                  }}
                  value={currentPage}
                  countPages={Math.ceil((searchResult?.count ?? 0) / pageSize)}
                />
              </div>
            )}
          </div>
        </>
      ) : showLoader ? (
        <div className="col-span-1 flex justify-center py-16 md:col-span-12">
          <Loader type={'infinite-spinner'} size={'large'} />
        </div>
      ) : null}
    </>
  );
};
